/**
 * Spec Phase 11 Slice 11D — Generate Everything progress + post-
 * completion failed-section banner.
 *
 * While running: floating bottom-right card with progress bar +
 * Cancel. On terminal status (completed / failed / cancelled) the
 * same card stays visible with a summary line + a Dismiss button
 * so a successful run doesn't disappear into a 5s toast. Failed
 * sections additionally surface in a rose-tinted dismissable
 * banner stacked above the status card.
 */
import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, X, XCircle } from 'lucide-react';
import { useSpecStore } from '@/store/specStore';

// Safety net: if the backend's terminal status never arrives
// after Cancel (worker crashed, DB row stuck at 'running', poll
// never observes 'cancelled'), force-clear the local cancelling
// state so the button doesn't sit at "Cancelling…" forever. The
// backend cancel endpoint also force-cancels stale jobs server-
// side; this is the client-side fallback.
const _CANCEL_TIMEOUT_MS = 30_000;

const _RUNNING_STATUSES = new Set(['queued', 'running']);
const _TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled']);

function _humanSection(kind: string): string {
  return kind.replace(/_/g, ' ');
}

export default function GenerateEverythingProgress(): JSX.Element | null {
  const status = useSpecStore((s) => s.generateEverythingStatus);
  const failures = useSpecStore((s) => s.generateEverythingFailures);
  const cancelGE = useSpecStore((s) => s.cancelGenerateEverything);
  const dismissFailures = useSpecStore(
    (s) => s.dismissGenerateEverythingFailures,
  );
  const dismissStatusCard = useSpecStore(
    (s) => s.dismissGenerateEverythingStatusCard,
  );

  // Local "cancelling" state — flips true immediately on click so
  // the button shows "Cancelling…" without waiting for the next
  // poll tick. The backend cancel mechanism (in-process registry
  // flag at spec_generate_everything.py:743) only checks at
  // section boundaries, so the worker can take up to one section's
  // wall-clock budget (now bounded at 120s per-section) before it
  // writes status='cancelled' to the DB. Without this state the
  // Cancel button stays visually unchanged the entire time and
  // drafters reasonably conclude "Cancel does nothing." Cleared
  // when the poll surfaces a terminal status (cancelled / completed
  // / failed) so the button doesn't get stuck if the worker
  // happened to finish the last section just as Cancel was
  // clicked. Local component state (not the store) because no
  // other component cares.
  const [cancelling, setCancelling] = useState(false);
  const cancelTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const _clearCancelTimeout = () => {
    if (cancelTimeoutRef.current !== null) {
      clearTimeout(cancelTimeoutRef.current);
      cancelTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    if (status && _TERMINAL_STATUSES.has(status.status)) {
      setCancelling(false);
      _clearCancelTimeout();
    }
  }, [status]);

  // Tear down any pending safety timeout on unmount so a stale
  // ref doesn't fire after the component has been removed from
  // the tree (the drafter navigated away mid-cancel).
  useEffect(() => _clearCancelTimeout, []);

  const handleCancel = async () => {
    if (cancelling) return;
    setCancelling(true);
    // Safety net — if the backend cancel hangs (worker crashed,
    // poll never observes a terminal status), force-clear the
    // local cancelling state after 30s so the button isn't stuck.
    _clearCancelTimeout();
    cancelTimeoutRef.current = setTimeout(() => {
      setCancelling(false);
      cancelTimeoutRef.current = null;
    }, _CANCEL_TIMEOUT_MS);
    try {
      await cancelGE();
    } catch {
      // The store action already surfaces a toast on failure;
      // reset the local flag so the drafter can retry the click.
      setCancelling(false);
      _clearCancelTimeout();
    }
    // On success we stay in cancelling state until either the
    // poll detects the terminal status (useEffect above clears it)
    // or the 30s safety timeout fires.
  };

  if (!status && failures.length === 0) return null;

  const isRunning = !!status && _RUNNING_STATUSES.has(status.status);
  const isTerminal = !!status && _TERMINAL_STATUSES.has(status.status);

  return (
    <div className="fixed bottom-4 right-4 z-30 flex flex-col gap-2">
      {isRunning && status && (
        <div
          className="w-80 rounded-md border border-zinc-300 bg-white shadow-md px-3 py-2 text-xs text-zinc-700"
          data-testid="spec-ge-progress"
          role="status"
          aria-live="polite"
        >
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1 font-medium">
              <Loader2 className="h-3 w-3 animate-spin" />
              Generating everything · {status.progress.completed}/
              {status.progress.total_sections} done
            </span>
            <button
              type="button"
              className="text-[11px] text-zinc-500 hover:text-zinc-900 underline disabled:cursor-not-allowed disabled:no-underline disabled:text-zinc-400"
              onClick={() => void handleCancel()}
              disabled={cancelling}
              data-testid="spec-ge-cancel"
              data-cancelling={cancelling ? 'true' : 'false'}
            >
              {cancelling ? 'Cancelling…' : 'Cancel'}
            </button>
          </div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded bg-zinc-100">
            <div
              className="h-full bg-zinc-700 transition-all"
              style={{ width: `${status.progress.percent}%` }}
            />
          </div>
          {status.progress.current_section && (
            <p className="mt-1 text-[11px] text-zinc-500">
              Currently:{' '}
              {_humanSection(status.progress.current_section)}
            </p>
          )}
          {cancelling && (
            <p
              className="mt-1 text-[11px] text-amber-700"
              data-testid="spec-ge-cancelling-hint"
            >
              Will stop after the current section completes
              (up to 2 minutes). Already-generated sections are kept.
            </p>
          )}
        </div>
      )}

      {/* Sticky terminal-status card. Replaces the auto-disappear
          behaviour so a successful run is visibly acknowledged
          until the drafter clicks Dismiss. The card colours +
          icon vary by terminal kind so the outcome is unambiguous. */}
      {isTerminal && status && (
        <div
          className={
            'w-80 rounded-md border shadow-md px-3 py-2 text-xs '
            + (status.status === 'completed'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : status.status === 'cancelled'
                ? 'border-zinc-300 bg-white text-zinc-700'
                : 'border-rose-200 bg-rose-50 text-rose-800')
          }
          data-testid="spec-ge-terminal-card"
          data-status={status.status}
          role="status"
        >
          <div className="flex items-start justify-between gap-2">
            <span className="flex items-center gap-1 font-medium">
              {status.status === 'completed' ? (
                <CheckCircle2 className="h-3 w-3" />
              ) : status.status === 'cancelled' ? (
                <XCircle className="h-3 w-3" />
              ) : (
                <AlertTriangle className="h-3 w-3" />
              )}
              {status.status === 'completed'
                ? `Generated ${status.progress.completed} of ${status.progress.total_sections}`
                : status.status === 'cancelled'
                  ? `Cancelled · ${status.progress.completed} of ${status.progress.total_sections} kept`
                  : `Generate everything failed`}
            </span>
            <button
              type="button"
              className="rounded-sm p-0.5 opacity-70 hover:bg-black/5 hover:opacity-100"
              onClick={dismissStatusCard}
              aria-label="Dismiss generate-everything status"
              data-testid="spec-ge-terminal-dismiss"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
          {status.status === 'completed' && status.progress.failed > 0 && (
            <p className="mt-1 text-[11px] opacity-80">
              {status.progress.failed} section
              {status.progress.failed === 1 ? '' : 's'} failed — see banner.
            </p>
          )}
          {status.status === 'failed' && status.error_message && (
            <p className="mt-1 text-[11px] opacity-80">
              {status.error_message.slice(0, 160)}
            </p>
          )}
        </div>
      )}

      {failures.length > 0 && (
        <div
          className="w-80 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800"
          role="alert"
          data-testid="spec-ge-failures-banner"
        >
          <div className="flex items-start justify-between gap-2">
            <span className="flex items-center gap-1 font-medium">
              <AlertTriangle className="h-3 w-3" />
              {failures.length} section{failures.length === 1 ? '' : 's'}
              {' '}failed
            </span>
            <button
              type="button"
              className="rounded-sm p-0.5 text-rose-500 hover:bg-rose-100"
              onClick={() => dismissFailures()}
              aria-label="Dismiss failures banner"
              data-testid="spec-ge-failures-dismiss"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
          <ul className="mt-1 space-y-0.5 text-[11px]">
            {failures.slice(0, 5).map((f, i) => (
              <li key={i}>
                <span className="font-mono">{f.section_kind}</span>
                {' — '}
                {f.error_code ?? 'failed'}
                {f.message ? `: ${f.message.slice(0, 80)}` : ''}
              </li>
            ))}
            {failures.length > 5 && (
              <li className="text-[10px] opacity-75">
                +{failures.length - 5} more — see diagnostic panel.
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
